import { FC } from "react";
import Container from "react-bootstrap/Container";
import ButtonGroup from "react-bootstrap/esm/ButtonGroup";
import { FaEdit, FaEnvelope } from "react-icons/fa";
import { useRouteParams } from "app/route/route";
import { NavigationScreen } from "app/component/screen/NavigationScreen";
import { RouteBreadcrumbs } from "app/route/RouteBreadcrumbs";
import {
    editReportVersionScopeRoute,
    reportVersionScopeRequestInformationEmailSendRoute,
    reportVersionScopeRoute,
} from "app/route/Routes";
import { useReportVersionScopeQuery } from "app/api/graph/types";
import { reportVersionGraphId } from "app/api/graph/helpers";
import { LoadingScreen } from "app/component/screen/LoadingScreen";
import { ErrorScreen } from "app/component/screen/ErrorScreen";
import { HtmlContent } from "app/component/util/HtmlContent";
import { Toolbar, ToolbarButtons, ToolbarTitle } from "app/component/util/Toolbar";
import { ButtonLink } from "app/component/button/ButtonLink";
import { useGeneralPermissions } from "app/api/graph/hook/use-general-permissions";
import { useReportVersionPermissions } from "app/api/graph/hook/use-report-version-permissions";

export const ReportVersionScopeScreen: FC = () => {
    const { reportId, versionId } = useRouteParams(reportVersionScopeRoute);
    const { data, loading, error } = useReportVersionScopeQuery({
        notifyOnNetworkStatusChange: true,
        variables: { versionId: reportVersionGraphId(versionId) },
    });
    const scope = data?.reportVersion?.scope;
    const { canUpdate } = useGeneralPermissions(scope?.permissions);
    const { canSendRequestInformationEmail } = useReportVersionPermissions(data?.reportVersion?.permissions);

    if (loading && !scope) {
        return <LoadingScreen />;
    }
    if (error) {
        return <ErrorScreen title="Error" message={error.message} />;
    }
    if (!scope) {
        return <ErrorScreen title="Error" message="Not found." />;
    }

    return (
        <NavigationScreen>
            <Container>
                <RouteBreadcrumbs route={reportVersionScopeRoute} />

                <Toolbar>
                    <ToolbarTitle>
                        <h1>Scope</h1>
                    </ToolbarTitle>
                    <ToolbarButtons>
                        {canUpdate ? (
                            <ButtonGroup className="me-2">
                                <ButtonLink
                                    to={editReportVersionScopeRoute.build({ reportId, versionId })}
                                    variant="warning"
                                >
                                    <FaEdit /> Edit
                                </ButtonLink>
                            </ButtonGroup>
                        ) : null}

                        {canSendRequestInformationEmail ? (
                            <ButtonGroup>
                                <ButtonLink
                                    to={reportVersionScopeRequestInformationEmailSendRoute.build({
                                        reportId,
                                        versionId,
                                    })}
                                    variant="warning"
                                >
                                    <FaEnvelope /> Request information
                                </ButtonLink>
                            </ButtonGroup>
                        ) : null}
                    </ToolbarButtons>
                </Toolbar>

                <h2>In scope</h2>
                <HtmlContent html={scope.inScope ?? "-"} />

                <h2>Out of scope</h2>
                <HtmlContent html={scope.outOfScope ?? "-"} />
            </Container>
        </NavigationScreen>
    );
};
